import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { ConfirmModal } from '../components/ConfirmModal';

type ConfirmOptions = {
  title: string;
  message: string;
  confirmText?: string;
  type?: 'danger' | 'warning' | 'info';
};

type DialogField = {
  name: string;
  label: string;
  type?: 'text' | 'number' | 'url' | 'textarea';
  initialValue?: string;
  placeholder?: string;
  required?: boolean;
};

type FormOptions = {
  title: string;
  message?: string;
  confirmText?: string;
  fields: DialogField[];
};

type DialogRequest =
  | { kind: 'confirm'; options: ConfirmOptions; resolve: (result: boolean) => void }
  | { kind: 'form'; options: FormOptions; resolve: (result: Record<string, string> | null) => void };

type AppDialogApi = {
  confirmAction: (options: ConfirmOptions) => Promise<boolean>;
  form: (options: FormOptions) => Promise<Record<string, string> | null>;
  requestInput: (options: Omit<FormOptions, 'fields'> & { label: string; initialValue?: string; placeholder?: string; multiline?: boolean; required?: boolean }) => Promise<string | null>;
};

const AppDialogContext = createContext<AppDialogApi | null>(null);

export const useAppDialogs = () => {
  const context = useContext(AppDialogContext);
  if (!context) throw new Error('useAppDialogs debe usarse dentro de AppDialogProvider.');
  return context;
};

export const AppDialogProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [active, setActive] = useState<DialogRequest | null>(null);
  const activeRef = useRef<DialogRequest | null>(null);
  const queueRef = useRef<DialogRequest[]>([]);

  const enqueue = useCallback((request: DialogRequest) => {
    if (activeRef.current) {
      queueRef.current.push(request);
      return;
    }
    activeRef.current = request;
    setActive(request);
  }, []);

  const finish = useCallback((result: boolean | Record<string, string> | null) => {
    const current = activeRef.current;
    if (!current) return;
    current.resolve(result as never);
    const next = queueRef.current.shift() || null;
    activeRef.current = next;
    setActive(next);
  }, []);

  useEffect(() => () => {
    activeRef.current?.resolve(activeRef.current.kind === 'confirm' ? false : null);
    queueRef.current.forEach((request) => request.resolve(request.kind === 'confirm' ? false : null) as never);
  }, []);

  const confirmAction = useCallback((options: ConfirmOptions) => new Promise<boolean>((resolve) => {
    enqueue({ kind: 'confirm', options, resolve });
  }), [enqueue]);

  const form = useCallback((options: FormOptions) => new Promise<Record<string, string> | null>((resolve) => {
    enqueue({ kind: 'form', options, resolve });
  }), [enqueue]);

  const requestInput = useCallback(async (options: Omit<FormOptions, 'fields'> & { label: string; initialValue?: string; placeholder?: string; multiline?: boolean; required?: boolean }) => {
    const result = await form({
      title: options.title,
      message: options.message,
      confirmText: options.confirmText,
      fields: [{
        name: 'value',
        label: options.label,
        type: options.multiline ? 'textarea' : 'text',
        initialValue: options.initialValue,
        placeholder: options.placeholder,
        required: options.required,
      }],
    });
    return result?.value ?? null;
  }, [form]);

  const api = React.useMemo(() => ({ confirmAction, form, requestInput }), [confirmAction, form, requestInput]);

  return (
    <AppDialogContext.Provider value={api}>
      {children}
      {active?.kind === 'confirm' && (
        <ConfirmModal
          isOpen
          title={active.options.title}
          message={active.options.message}
          confirmText={active.options.confirmText}
          type={active.options.type || 'warning'}
          onClose={() => finish(false)}
          onConfirm={() => finish(true)}
        />
      )}
      {active?.kind === 'form' && (
        <DialogFormModal request={active} onClose={() => finish(null)} onSubmit={(values) => finish(values)} />
      )}
    </AppDialogContext.Provider>
  );
};

const DialogFormModal: React.FC<{
  request: Extract<DialogRequest, { kind: 'form' }>;
  onClose: () => void;
  onSubmit: (values: Record<string, string>) => void;
}> = ({ request, onClose, onSubmit }) => {
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(
    request.options.fields.map((field) => [field.name, field.initialValue || '']),
  ));

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        onSubmit={(event) => { event.preventDefault(); onSubmit(values); }}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-theme bg-theme-card p-5 text-theme-main shadow-2xl sm:p-6"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id="app-dialog-title" className="text-lg font-semibold">{request.options.title}</h2>
            {request.options.message && <p className="mt-1 text-sm text-theme-muted">{request.options.message}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-theme-muted hover:bg-theme-base" aria-label="Cerrar"><X size={19}/></button>
        </div>
        <div className="space-y-4">
          {request.options.fields.map((field) => {
            const className = 'mt-1.5 w-full rounded-xl border border-theme bg-theme-card px-3 py-2 text-sm text-theme-main placeholder:text-theme-muted focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20';
            return <label key={field.name} className="block text-sm font-medium text-theme-main">
              {field.label}
              {field.type === 'textarea' ? (
                <textarea autoFocus={request.options.fields[0]?.name === field.name} required={field.required} rows={5} value={values[field.name] || ''} placeholder={field.placeholder} onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.target.value }))} className={className}/>
              ) : (
                <input autoFocus={request.options.fields[0]?.name === field.name} required={field.required} type={field.type || 'text'} value={values[field.name] || ''} placeholder={field.placeholder} onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.target.value }))} className={className}/>
              )}
            </label>;
          })}
        </div>
        <div className="mt-6 flex justify-end gap-2 border-t border-theme pt-4">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-theme-main hover:bg-theme-base">Cancelar</button>
          <button type="submit" className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">{request.options.confirmText || 'Guardar'}</button>
        </div>
      </form>
    </div>
  );
};
